import { useMemo, useState } from "react"

import { isFunction, snakeCase, values } from "lodash"
import { Divider, message, notification } from "antd"
import { useSelector } from "react-redux"

import SectionGroupForm from "./SectionGroupForm"
import { selectTranslate } from "../../../reducers/LocaleReducer/LocalReducer"
import { getIdOrNull, isEmptyArray, isObject, isOptimistic } from "../../../utils"
import { SectionGroup, SectionGroupValidators } from "../../../utils/entities/sectionGroup"
import { isValid } from "../../../utils/validators"
import FormFooter from "../../../Components/antd/Forms/FormFooter"
import { selectUser } from "../../../reducers/UserReducer"
import { isInstitutionUser } from "../../../utils/roles"

message.config({
  top: 20,
  duration: 3,
  maxCount: 1
})

const SingleSectionGroupForm = ({ data, sectionsToHighlight, disabled, onSubmit, onDelete, onCancel }) => {
  const user = useSelector(selectUser)
  const t = useSelector(selectTranslate)

  const [formData, setFormData] = useState(isObject(data) ? new SectionGroup(data) : data)
  const [errorFields, setErrorFields] = useState([])

  const handleSubmit = event => {
    event.preventDefault()

    if (isInstitutionUser(user) && formData.sectors.length === 0) {
      formData.institution = getIdOrNull(user.institutions[0])
    }

    const errors = isValid(formData, values(SectionGroupValidators))
    setErrorFields(errors.keys)

    if (errors.keys.length > 0) {
      const errorMessages = t('common_form.error_fields', {
        fields: errors.keys.map(key => t('common.' + snakeCase(key))).join(', ')
      })

      notification.error({ message: errorMessages })
    } else {
      onSubmit(new SectionGroup(formData))
    }
  }

  const updateForm = newData => setFormData(form => new SectionGroup({ ...form, ...newData }))

  const isSubmitDisabled = useMemo(() => {
    return !formData.name || isEmptyArray(formData.sections) || disabled
  }, [formData.name, formData.sections, disabled])

  const submitButtonProps = useMemo(() => ({
    text: t('modal.ok_button.save'),
    disabled: isSubmitDisabled,
    title: isSubmitDisabled && t('form_modal.add_button.disabled_tooltip', {
      fields: [
        t('common.name'),
        t('common.sections')
      ].join(', ')
    })
  }), [isSubmitDisabled, t])

  return (
    <div className="m-16 pd-16">
      <SectionGroupForm
        data={formData}
        sectionsToHighlight={sectionsToHighlight}
        disabled={disabled}
        onChange={updateForm}
        errorFields={errorFields}
      />

      <Divider className='divider-4' />

      <FormFooter
        cancel={{ text: t('modal.cancel_button'), disabled }}
        remove={!isOptimistic(formData) && { popover: { content: t('section_group_form_footer.popover.delete'), title: t('common.delete') } }}
        submit={submitButtonProps}
        onCancel={isFunction(onCancel) ? onCancel : null}
        onDelete={isFunction(onDelete) ? onDelete : null}
        onSubmit={handleSubmit}
      />
    </div>
  )
}

export default SingleSectionGroupForm